import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type Phase = "steps" | "email" | "success";
type PlanType = "share" | "direct" | "undecided" | "";

type CanvasData = {
  coreMessage: string;
  targetAudience: string;
  channels: string[];
  followerCount: string;
  monetization: string[];
  painPoints: string[];
  planType: PlanType;
  planTier: string;
};

type EmailData = { name: string; email: string; phone: string };

/* ─── Options ─── */
const channelOptions = [
  { id: "sns", label: "인스타그램 등 SNS", icon: "ri-instagram-line", hasFollower: true },
  { id: "offline", label: "오프라인 전시/공연장", icon: "ri-store-2-line", hasFollower: false },
  { id: "blog", label: "블로그 / 브런치 플랫폼", icon: "ri-article-line", hasFollower: false },
  { id: "none", label: "아직 보여줄 공간이 없음", icon: "ri-question-mark", hasFollower: false },
];

const monetizationOptions = [
  { id: "goods", label: "물리적 굿즈", icon: "ri-t-shirt-2-line", desc: "문구, 패션, 리빙 소품" },
  { id: "original", label: "오리지널 원화", icon: "ri-gallery-line", desc: "원화, 조각, 한정판 판화" },
  { id: "digital", label: "디지털 콘텐츠", icon: "ri-film-line", desc: "웹툰, 음원, 유료 뉴스레터" },
  { id: "experience", label: "오프라인 경험", icon: "ri-map-pin-line", desc: "전시, 공연, 워크샵 티켓" },
  { id: "fandom", label: "팬덤 구독", icon: "ri-vip-crown-line", desc: "멤버십, 프라이빗 커뮤니티" },
];

const painPointOptions = [
  { id: "capital", label: "초기 자본", icon: "ri-money-dollar-circle-line", desc: "프로젝트 시작 비용" },
  { id: "webapp", label: "나만의 웹/앱", icon: "ri-smartphone-line", desc: "개인 플랫폼 구축" },
  { id: "legal", label: "법률/회계 지식", icon: "ri-scales-3-line", desc: "저작권, 세금 처리" },
  { id: "marketing", label: "마케팅 노하우", icon: "ri-megaphone-line", desc: "홍보 및 세일즈 전략" },
];

const shareTiers = [
  {
    id: "incubate",
    name: "인큐베이팅형",
    sub: "아르트레너 전액 투자",
    tags: ["초기 투자 0원", "쉐어 40–50%"],
    icon: "ri-fire-line",
    color: "#F97316",
    glow: "rgba(249,115,22,0.18)",
    border: "rgba(249,115,22,0.35)",
  },
  {
    id: "partner",
    name: "파트너형",
    sub: "리스크 분담형 공동성장",
    tags: ["투자 300–500만원", "쉐어 25–30%"],
    icon: "ri-shake-hands-line",
    color: "#A855F7",
    glow: "rgba(168,85,247,0.18)",
    border: "rgba(168,85,247,0.45)",
    popular: true,
  },
  {
    id: "scale",
    name: "스케일업형",
    sub: "자체 자본 보유 아티스트용",
    tags: ["투자 협의", "쉐어 10–15%"],
    icon: "ri-seedling-line",
    color: "#14F1D9",
    glow: "rgba(20,241,217,0.15)",
    border: "rgba(20,241,217,0.3)",
  },
];

const directTiers = [
  {
    id: "single",
    name: "싱글 채널",
    sub: "하나의 채널을 완벽하게",
    tags: ["예상 1주", "소규모"],
    icon: "ri-focus-3-line",
    color: "#14F1D9",
    glow: "rgba(20,241,217,0.15)",
    border: "rgba(20,241,217,0.3)",
  },
  {
    id: "multi",
    name: "멀티 채널",
    sub: "브랜드 + 판매 + 마케팅 한 번에",
    tags: ["예상 2주", "중규모"],
    icon: "ri-layout-grid-line",
    color: "#A855F7",
    glow: "rgba(168,85,247,0.18)",
    border: "rgba(168,85,247,0.45)",
    popular: true,
  },
  {
    id: "platform",
    name: "풀 플랫폼",
    sub: "복합 기능 · 커스텀 플랫폼",
    tags: ["예상 4주", "대규모"],
    icon: "ri-stack-line",
    color: "#F97316",
    glow: "rgba(249,115,22,0.15)",
    border: "rgba(249,115,22,0.3)",
  },
];

const planTierLabelMap: Record<string, string> = {
  incubate: "인큐베이팅형",
  partner: "파트너형",
  scale: "스케일업형",
  single: "싱글 채널",
  multi: "멀티 채널",
  platform: "풀 플랫폼",
};

const planTypeLabel = (t: PlanType) =>
  t === "share" ? "수익 쉐어형" : t === "direct" ? "개발비 직접결제형" : "상담 후 결정";

/* ─── Label maps ─── */
const channelLabelMap: Record<string, string> = {
  sns: "인스타그램 등 SNS", offline: "오프라인 전시/공연장", blog: "블로그/브런치", none: "아직 채널 없음",
};
const monetizationLabelMap: Record<string, string> = {
  goods: "물리적 굿즈", original: "오리지널 원화", digital: "디지털 콘텐츠", experience: "오프라인 경험", fandom: "팬덤 구독",
};
const painPointLabelMap: Record<string, string> = {
  capital: "초기 자본", webapp: "나만의 웹/앱", legal: "법률/회계 지식", marketing: "마케팅 노하우",
};

/* ─── Utils ─── */
const toggleArray = (arr: string[], val: string) =>
  arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];

/* ─── Steps config ─── */
const STEPS = [
  { num: 1, label: "가치 제안", icon: "ri-lightbulb-flash-line", color: "text-brand-purple", bg: "rgba(168,85,247,0.15)", border: "rgba(168,85,247,0.3)" },
  { num: 2, label: "타겟 팬덤", icon: "ri-group-line", color: "text-brand-teal", bg: "rgba(20,241,217,0.1)", border: "rgba(20,241,217,0.3)" },
  { num: 3, label: "채널 진단", icon: "ri-wifi-line", color: "text-brand-orange", bg: "rgba(249,115,22,0.1)", border: "rgba(249,115,22,0.3)" },
  { num: 4, label: "수익 모델", icon: "ri-bar-chart-2-line", color: "text-brand-purple", bg: "rgba(168,85,247,0.1)", border: "rgba(168,85,247,0.3)" },
  { num: 5, label: "빌딩 포인트", icon: "ri-tools-line", color: "text-brand-teal", bg: "rgba(20,241,217,0.1)", border: "rgba(20,241,217,0.3)" },
  { num: 6, label: "플랜 선택", icon: "ri-price-tag-3-line", color: "text-brand-orange", bg: "rgba(249,115,22,0.1)", border: "rgba(249,115,22,0.3)" },
];

const TOTAL_STEPS = 6;

const initialData: CanvasData = {
  coreMessage: "", targetAudience: "", channels: [], followerCount: "",
  monetization: [], painPoints: [], planType: "", planTier: "",
};

export default function DiagnosisPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetPlanType = searchParams.get("planType") as PlanType | null;
  const presetPlanTier = searchParams.get("planTier") ?? "";

  const [phase, setPhase] = useState<Phase>("steps");
  const [step, setStep] = useState(1);
  const [visible, setVisible] = useState(true);
  const [data, setData] = useState<CanvasData>({
    ...initialData,
    planType: (presetPlanType as PlanType) ?? "",
    planTier: presetPlanTier,
  });
  const [emailData, setEmailData] = useState<EmailData>({ name: "", email: "", phone: "" });
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const progress = (step / TOTAL_STEPS) * 100;

  const canNext = () => {
    if (step === 1) return data.coreMessage.trim().length > 0;
    if (step === 2) return data.targetAudience.trim().length > 0;
    if (step === 3) return data.channels.length > 0;
    if (step === 4) return data.monetization.length > 0;
    if (step === 5) return data.painPoints.length > 0;
    if (step === 6) return data.planType !== "";
    return true;
  };

  const transitionTo = (next: number | Phase) => {
    setVisible(false);
    setTimeout(() => {
      if (typeof next === "number") setStep(next);
      else setPhase(next);
      setVisible(true);
    }, 220);
  };

  const handleNext = () => {
    if (!canNext()) return;
    if (step < TOTAL_STEPS) transitionTo(step + 1);
    else transitionTo("email");
  };

  const handlePrev = () => {
    if (step > 1) transitionTo(step - 1);
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!emailData.email.trim()) return;
    setEmailSubmitting(true);
    setEmailError("");
    try {
      const planLabel =
        data.planType === "undecided"
          ? "상담 후 결정"
          : `${planTypeLabel(data.planType)} · ${planTierLabelMap[data.planTier] ?? data.planTier}`;
      const body = new URLSearchParams({
        name: emailData.name,
        email: emailData.email,
        phone: emailData.phone,
        "핵심 메시지": data.coreMessage,
        "타겟 팬덤": data.targetAudience,
        "채널": data.channels.map((c) => channelLabelMap[c] ?? c).join(", "),
        "팔로워 수": data.followerCount,
        "수익 모델": data.monetization.map((m) => monetizationLabelMap[m] ?? m).join(", "),
        "빌딩 포인트": data.painPoints.map((p) => painPointLabelMap[p] ?? p).join(", "),
        "희망 플랜": planLabel,
      });
      await fetch("https://readdy.ai/api/form/d72im0kij602egd95is0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      transitionTo("success");
    } catch {
      setEmailError("제출 중 오류가 발생했어요. 잠시 후 다시 시도해주세요.");
      setEmailSubmitting(false);
    }
  };

  const handleReset = () => {
    setVisible(false);
    setTimeout(() => {
      setPhase("steps"); setStep(1);
      setData(initialData); setEmailData({ name: "", email: "", phone: "" });
      setEmailError(""); setVisible(true);
    }, 220);
  };

  const currentStepCfg = STEPS[step - 1];

  /* ── helper: tier card ── */
  const renderTierCards = (tiers: typeof shareTiers) =>
    tiers.map((t) => {
      const active = data.planTier === t.id;
      return (
        <button
          key={t.id}
          onClick={() => setData((d) => ({ ...d, planTier: t.id }))}
          className="relative flex flex-col gap-3 p-4 rounded-2xl border text-left transition-all duration-200 cursor-pointer"
          style={
            active
              ? { background: t.glow, borderColor: t.border, boxShadow: `0 0 16px ${t.glow}` }
              : { background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.08)" }
          }
        >
          {(t as { popular?: boolean }).popular && (
            <span
              className="absolute -top-2.5 left-4 text-xs font-display font-bold text-white px-2.5 py-0.5 rounded-full whitespace-nowrap"
              style={{ background: "linear-gradient(90deg, #A855F7, #7C3AED)" }}
            >
              POPULAR
            </span>
          )}
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: t.glow }}>
              <i className={`${t.icon} text-base`} style={{ color: t.color }} />
            </div>
            <div>
              <p className="text-sm font-display font-bold text-white">{t.name}</p>
              <p className="text-xs text-white/40">{t.sub}</p>
            </div>
            {active && (
              <div className="ml-auto w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: t.color }}>
                <i className="ri-check-line text-xs text-white" />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {t.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded-full font-body"
                style={
                  active
                    ? { background: `${t.color}20`, color: t.color, border: `1px solid ${t.color}40` }
                    : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.35)", border: "1px solid rgba(255,255,255,0.08)" }
                }
              >
                {tag}
              </span>
            ))}
          </div>
        </button>
      );
    });

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(160deg, #06080F 0%, #0D0816 50%, #06080F 100%)" }}>
      <div className="fixed inset-0 pointer-events-none z-0" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(168,85,247,0.08) 0%, transparent 70%)" }} />

      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4 md:px-8"
        style={{ background: "rgba(6,8,15,0.88)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
      >
        <a href="/" className="flex items-center gap-2 cursor-pointer">
          <img src="https://public.readdy.ai/ai/img_res/b51260fb-bf77-436f-865d-1c38bc3c656b.png" alt="ARTREPRENEUR" className="w-7 h-7 object-contain" />
          <span className="font-display font-bold text-sm tracking-widest text-white hidden sm:block">ARTREPRENEUR</span>
        </a>

        {phase === "steps" && (
          <div className="flex items-center gap-1.5">
            {STEPS.map((s) => (
              <div
                key={s.num}
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-display font-bold transition-all duration-300"
                style={
                  s.num === step
                    ? { background: "linear-gradient(135deg, #A855F7, #7C3AED)", color: "#fff", transform: "scale(1.1)" }
                    : s.num < step
                    ? { background: "rgba(168,85,247,0.25)", border: "1px solid rgba(168,85,247,0.4)", color: "rgba(255,255,255,0.6)" }
                    : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.2)" }
                }
              >
                {s.num < step ? <i className="ri-check-line text-xs" /> : s.num}
              </div>
            ))}
          </div>
        )}

        {phase === "email" && <span className="text-xs text-white/40 font-body tracking-wide">거의 다 왔어요!</span>}

        <button
          onClick={() => navigate("/")}
          className="w-9 h-9 flex items-center justify-center rounded-full text-white/50 hover:text-white transition-colors cursor-pointer"
          style={{ background: "rgba(255,255,255,0.05)" }}
        >
          <i className="ri-close-line text-lg" />
        </button>
      </header>

      {/* Progress Bar */}
      <div className="fixed top-14 left-0 right-0 z-50 h-0.5" style={{ background: "rgba(255,255,255,0.05)" }}>
        <div
          className="h-full transition-all duration-500 ease-out"
          style={{
            width: phase === "email" || phase === "success" ? "100%" : `${progress}%`,
            background: "linear-gradient(to right, #A855F7, #14F1D9)",
          }}
        />
      </div>

      {/* Main */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 pt-20 pb-28">
        <div
          className="w-full max-w-2xl"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0px)" : "translateY(18px)",
            transition: "opacity 0.22s ease, transform 0.22s ease",
          }}
        >
          {/* ── Success ── */}
          {phase === "success" && (
            <div className="text-center py-12">
              <div
                className="w-20 h-20 rounded-full mx-auto mb-8 flex items-center justify-center"
                style={{ background: "radial-gradient(circle, rgba(168,85,247,0.25) 0%, rgba(168,85,247,0.08) 70%)", border: "1px solid rgba(168,85,247,0.4)" }}
              >
                <i className="ri-check-double-line text-3xl text-brand-purple" />
              </div>
              <span className="inline-block text-xs font-display font-medium tracking-widest uppercase text-brand-teal mb-6 px-3 py-1 rounded-full border border-brand-teal/30 bg-brand-teal/5">
                접수 완료
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-5 leading-snug">
                아르트레너 빌더팀이<br />곧 연락드릴게요!
              </h2>
              <p className="text-white/50 text-base font-body mb-3 leading-loose max-w-md mx-auto">
                진단 결과를 바탕으로 전문 빌더팀이 <strong className="text-white/80">48시간 내</strong>에 맞춤형 빌딩 플랜을 가지고 연락드릴게요.
              </p>

              {/* Selected plan highlight */}
              {data.planType !== "" && (
                <div
                  className="max-w-sm mx-auto mt-7 mb-8 p-5 rounded-2xl text-left"
                  style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.25)" }}
                >
                  <p className="text-xs text-white/35 font-body mb-3 tracking-wide uppercase">희망 플랜</p>
                  {data.planType === "undecided" ? (
                    <p className="text-white font-display font-semibold text-base">상담 후 결정</p>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-body mb-1" style={{ color: data.planType === "share" ? "#A855F7" : "#14F1D9" }}>
                          {planTypeLabel(data.planType)}
                        </p>
                        <p className="text-white font-display font-bold text-lg">
                          {planTierLabelMap[data.planTier] ?? data.planTier}
                        </p>
                      </div>
                      <a
                        href={`/?tab=${data.planType}#pricing`}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-display font-semibold text-white whitespace-nowrap cursor-pointer transition-all"
                        style={{ background: "rgba(168,85,247,0.25)", border: "1px solid rgba(168,85,247,0.4)" }}
                      >
                        플랜 상세 보기
                        <i className="ri-arrow-right-line" />
                      </a>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="/"
                  className="px-7 py-3 rounded-full text-sm font-display font-semibold text-white cursor-pointer whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg, #A855F7, #7C3AED)", boxShadow: "0 0 24px rgba(168,85,247,0.35)" }}
                >
                  홈으로 돌아가기
                </a>
                <button
                  onClick={handleReset}
                  className="px-6 py-3 rounded-full text-sm font-body text-white/50 hover:text-white border border-white/10 hover:border-white/20 transition-colors cursor-pointer whitespace-nowrap"
                >
                  처음부터 다시 작성하기
                </button>
              </div>
            </div>
          )}

          {/* ── Email Form ── */}
          {phase === "email" && (
            <div className="flex flex-col gap-8">
              <div>
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-display font-semibold tracking-widest uppercase mb-5"
                  style={{ background: "rgba(20,241,217,0.08)", border: "1px solid rgba(20,241,217,0.25)", color: "#14F1D9" }}
                >
                  <i className="ri-checkbox-circle-line" />
                  진단 완료 · 연락처 입력
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white leading-snug mb-3">
                  맞춤 빌딩 플랜을<br />어디로 보내드릴까요?
                </h2>
                <p className="text-white/45 text-sm font-body leading-loose">
                  빌더팀이 진단 결과를 검토하고 <strong className="text-white/70">48시간 내</strong>에 직접 연락드릴게요.
                </p>
              </div>

              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 p-4 rounded-2xl" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <span className="text-xs text-white/30 font-body w-full mb-1">진단 요약</span>
                {data.channels.map((c) => (
                  <span key={c} className="text-xs px-2.5 py-1 rounded-full font-body text-brand-orange/80" style={{ background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)" }}>
                    {channelLabelMap[c] ?? c}
                  </span>
                ))}
                {data.monetization.map((m) => (
                  <span key={m} className="text-xs px-2.5 py-1 rounded-full font-body text-brand-purple/80" style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.2)" }}>
                    {monetizationLabelMap[m] ?? m}
                  </span>
                ))}
                {data.painPoints.map((p) => (
                  <span key={p} className="text-xs px-2.5 py-1 rounded-full font-body text-brand-teal/80" style={{ background: "rgba(20,241,217,0.07)", border: "1px solid rgba(20,241,217,0.2)" }}>
                    {painPointLabelMap[p] ?? p}
                  </span>
                ))}
                {data.planType && data.planType !== "undecided" && (
                  <span className="text-xs px-2.5 py-1 rounded-full font-body font-semibold text-white" style={{ background: "rgba(168,85,247,0.18)", border: "1px solid rgba(168,85,247,0.35)" }}>
                    {planTypeLabel(data.planType)} · {planTierLabelMap[data.planTier] ?? ""}
                  </span>
                )}
                {data.planType === "undecided" && (
                  <span className="text-xs px-2.5 py-1 rounded-full font-body text-white/50" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                    플랜 상담 후 결정
                  </span>
                )}
              </div>

              <form id="diagnosis-email-form" data-readdy-form className="flex flex-col gap-4" onSubmit={handleEmailSubmit}>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-display font-semibold tracking-wide text-white/50 uppercase">
                    이름 <span className="text-brand-teal">*</span>
                  </label>
                  <input
                    type="text" name="name" required placeholder="홍길동"
                    value={emailData.name} onChange={(e) => setEmailData((d) => ({ ...d, name: e.target.value }))}
                    className="w-full border border-white/10 focus:border-brand-purple/60 rounded-xl px-4 py-3.5 text-white placeholder-white/20 text-sm font-body outline-none transition-all"
                    style={{ background: "rgba(255,255,255,0.03)" }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-display font-semibold tracking-wide text-white/50 uppercase">
                    이메일 <span className="text-brand-teal">*</span>
                  </label>
                  <input
                    type="email" name="email" required placeholder="you@example.com"
                    value={emailData.email} onChange={(e) => setEmailData((d) => ({ ...d, email: e.target.value }))}
                    className="w-full border border-white/10 focus:border-brand-purple/60 rounded-xl px-4 py-3.5 text-white placeholder-white/20 text-sm font-body outline-none transition-all"
                    style={{ background: "rgba(255,255,255,0.03)" }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-display font-semibold tracking-wide text-white/50 uppercase">
                    연락처 <span className="text-white/25 font-normal normal-case">(선택)</span>
                  </label>
                  <input
                    type="tel" name="phone" placeholder="010-1234-5678"
                    value={emailData.phone} onChange={(e) => setEmailData((d) => ({ ...d, phone: e.target.value }))}
                    className="w-full border border-white/10 focus:border-brand-purple/60 rounded-xl px-4 py-3.5 text-white placeholder-white/20 text-sm font-body outline-none transition-all"
                    style={{ background: "rgba(255,255,255,0.03)" }}
                  />
                </div>
                {emailError && (
                  <p className="text-xs text-red-400 font-body flex items-center gap-1.5">
                    <i className="ri-error-warning-line" />{emailError}
                  </p>
                )}
                <p className="text-xs text-white/25 font-body leading-relaxed">
                  수집된 정보는 맞춤 빌딩 플랜 안내 목적으로만 사용되며, 제3자에게 제공되지 않아요.
                </p>
              </form>
            </div>
          )}

          {/* ── Steps ── */}
          {phase === "steps" && (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: currentStepCfg.bg, border: `1px solid ${currentStepCfg.border}` }}>
                  <i className={`${currentStepCfg.icon} ${currentStepCfg.color} text-lg`} />
                </div>
                <p className={`text-xs font-display font-semibold tracking-widest uppercase ${currentStepCfg.color}`}>
                  Step {step} of {TOTAL_STEPS} · {currentStepCfg.label}
                </p>
              </div>

              {/* Step 1 */}
              {step === 1 && (
                <div className="flex flex-col gap-6">
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-white leading-snug mb-3">당신의 예술은 무엇을 전달하나요?</h2>
                    <p className="text-white/50 text-sm font-body leading-loose">창작물이 세상에 전달하고 싶은 핵심 메시지나 감정은 무엇인가요?<br />나의 예술을 한 문장으로 정의해보세요.</p>
                  </div>
                  <div className="relative">
                    <textarea
                      className="w-full border border-white/10 focus:border-brand-purple/60 rounded-2xl px-5 py-4 text-white placeholder-white/25 text-sm font-body resize-none outline-none transition-all leading-loose"
                      placeholder="예: 바쁜 현대인들에게 잠시 멈춰 자신을 바라보는 고요한 순간을 선물하고 싶습니다."
                      rows={5} maxLength={200}
                      value={data.coreMessage} onChange={(e) => setData((d) => ({ ...d, coreMessage: e.target.value }))}
                      style={{ background: "rgba(255,255,255,0.03)" }}
                    />
                    <div className="absolute bottom-3 right-4 text-xs text-white/25">{data.coreMessage.length}/200</div>
                  </div>
                </div>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div className="flex flex-col gap-6">
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-white leading-snug mb-3">내 작품에 열광할 사람들은 누구인가요?</h2>
                    <p className="text-white/50 text-sm font-body leading-loose">
                      내 작품을 좋아하거나 좋아할 사람들은 어떤 취향을 가진 누구인가요?<br />
                      <span className="text-white/30">힌트: &quot;힙한 카페를 찾아다니는 20대 여성&quot;, &quot;사회 문제에 관심 많은 직장인&quot;</span>
                    </p>
                  </div>
                  <textarea
                    className="w-full border border-white/10 focus:border-brand-teal/60 rounded-2xl px-5 py-4 text-white placeholder-white/25 text-sm font-body resize-none outline-none transition-all leading-loose"
                    placeholder="예: 인테리어에 관심 많고 독립 서점을 즐겨 찾는 30대 직장인들..."
                    rows={5}
                    value={data.targetAudience} onChange={(e) => setData((d) => ({ ...d, targetAudience: e.target.value }))}
                    style={{ background: "rgba(255,255,255,0.03)" }}
                  />
                </div>
              )}

              {/* Step 3 */}
              {step === 3 && (
                <div className="flex flex-col gap-6">
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-white leading-snug mb-3">현재 팬들과 소통하는 곳은 어디인가요?</h2>
                    <p className="text-white/50 text-sm font-body leading-loose">해당하는 채널을 모두 선택해주세요 (다중 선택 가능)</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {channelOptions.map((ch) => {
                      const active = data.channels.includes(ch.id);
                      return (
                        <div key={ch.id}>
                          <button
                            onClick={() => setData((d) => ({ ...d, channels: toggleArray(d.channels, ch.id), followerCount: ch.id === "none" ? "" : d.followerCount }))}
                            className={`w-full flex items-center gap-3 px-4 py-4 rounded-2xl border text-sm font-body text-left transition-all cursor-pointer ${active ? "text-white border-brand-orange/50" : "text-white/50 border-white/10 hover:border-white/20 hover:text-white/70"}`}
                            style={active ? { background: "rgba(249,115,22,0.1)" } : { background: "rgba(255,255,255,0.03)" }}
                          >
                            <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${active ? "text-brand-orange" : "text-white/30"}`}
                              style={{ background: active ? "rgba(249,115,22,0.15)" : "rgba(255,255,255,0.05)" }}>
                              <i className={`${ch.icon} text-base`} />
                            </div>
                            <span className={active ? "text-white font-medium" : ""}>{ch.label}</span>
                            {active && <i className="ri-check-line text-brand-orange ml-auto" />}
                          </button>
                          {active && ch.hasFollower && (
                            <input
                              className="w-full mt-2 border border-brand-orange/30 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 outline-none transition-all"
                              placeholder="팔로워 수를 입력하세요 (예: 5,200명)"
                              value={data.followerCount} onChange={(e) => setData((d) => ({ ...d, followerCount: e.target.value }))}
                              style={{ background: "rgba(249,115,22,0.05)" }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 4 */}
              {step === 4 && (
                <div className="flex flex-col gap-6">
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-white leading-snug mb-3">어떤 형태로 수익을 만들고 싶나요?</h2>
                    <p className="text-white/50 text-sm font-body leading-loose">희망하는 상품화 방향을 모두 선택하세요 (다중 선택 가능)</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {monetizationOptions.map((opt) => {
                      const active = data.monetization.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          onClick={() => setData((d) => ({ ...d, monetization: toggleArray(d.monetization, opt.id) }))}
                          className={`flex items-center gap-2.5 px-4 py-3.5 rounded-2xl border text-sm font-body transition-all cursor-pointer whitespace-nowrap ${active ? "text-white border-brand-purple/60" : "text-white/50 border-white/10 hover:border-white/20"}`}
                          style={active ? { background: "linear-gradient(135deg, rgba(168,85,247,0.15), rgba(124,58,237,0.1))", boxShadow: "0 0 12px rgba(168,85,247,0.2)" } : { background: "rgba(255,255,255,0.03)" }}
                        >
                          <i className={`${opt.icon} text-base ${active ? "text-brand-purple" : "text-white/30"}`} />
                          <div className="text-left">
                            <div className={active ? "text-white font-medium" : ""}>{opt.label}</div>
                            <div className="text-xs text-white/30 mt-0.5">{opt.desc}</div>
                          </div>
                          {active && (
                            <div className="w-5 h-5 flex items-center justify-center rounded-full ml-1 flex-shrink-0" style={{ background: "rgba(168,85,247,0.3)" }}>
                              <i className="ri-check-line text-xs text-brand-purple" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 5 */}
              {step === 5 && (
                <div className="flex flex-col gap-6">
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-white leading-snug mb-3">가장 막막하고 부족한 부분은?</h2>
                    <p className="text-white/50 text-sm font-body leading-loose">아르트레너가 집중적으로 지원할 영역을 선택하세요 (다중 선택 가능)</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {painPointOptions.map((opt) => {
                      const active = data.painPoints.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          onClick={() => setData((d) => ({ ...d, painPoints: toggleArray(d.painPoints, opt.id) }))}
                          className={`flex flex-col gap-2.5 p-5 rounded-2xl border text-sm font-body transition-all cursor-pointer text-left ${active ? "text-white border-brand-teal/50" : "text-white/50 border-white/10 hover:border-white/20"}`}
                          style={active ? { background: "rgba(20,241,217,0.07)", boxShadow: "0 0 16px rgba(20,241,217,0.12)" } : { background: "rgba(255,255,255,0.03)" }}
                        >
                          <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${active ? "text-brand-teal" : "text-white/30"}`}
                            style={{ background: active ? "rgba(20,241,217,0.15)" : "rgba(255,255,255,0.05)" }}>
                            <i className={`${opt.icon} text-xl`} />
                          </div>
                          <div className={`font-semibold text-sm ${active ? "text-white" : ""}`}>{opt.label}</div>
                          <div className="text-xs text-white/35 leading-relaxed">{opt.desc}</div>
                          {active && <div className="flex items-center gap-1 text-xs text-brand-teal"><i className="ri-checkbox-circle-fill" /><span>선택됨</span></div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 6 — Plan Selection */}
              {step === 6 && (
                <div className="flex flex-col gap-7">
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-white leading-snug mb-3">
                      어떤 방식이 맞을 것 같으세요?
                    </h2>
                    <p className="text-white/50 text-sm font-body leading-loose">
                      빌더팀이 맞춤 제안을 드릴 때 참고합니다. 아직 확실하지 않아도 괜찮아요.
                    </p>
                    {/* Pre-filled badge */}
                    {presetPlanType && presetPlanTier && (
                      <div
                        className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full text-xs font-body"
                        style={{ background: "rgba(20,241,217,0.08)", border: "1px solid rgba(20,241,217,0.25)", color: "#14F1D9" }}
                      >
                        <i className="ri-price-tag-3-line" />
                        플랜 페이지에서 선택한 플랜이 미리 표시돼 있어요. 변경하셔도 됩니다.
                      </div>
                    )}
                  </div>

                  {/* Type selector */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { id: "share" as PlanType, label: "수익 쉐어형", desc: "아르트레너와 함께 투자하고 수익을 나눠요", icon: "ri-hand-coin-line", color: "#A855F7", glow: "rgba(168,85,247,0.15)", border: "rgba(168,85,247,0.45)" },
                      { id: "direct" as PlanType, label: "개발비 직접결제형", desc: "개발비를 내고 결과물을 100% 소유해요", icon: "ri-shield-check-line", color: "#14F1D9", glow: "rgba(20,241,217,0.12)", border: "rgba(20,241,217,0.35)" },
                      { id: "undecided" as PlanType, label: "아직 잘 모르겠어요", desc: "빌더팀과 상담 후 결정할게요", icon: "ri-question-line", color: "rgba(255,255,255,0.5)", glow: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.15)" },
                    ].map((t) => {
                      const active = data.planType === t.id;
                      return (
                        <button
                          key={t.id}
                          onClick={() => setData((d) => ({ ...d, planType: t.id, planTier: "" }))}
                          className="flex flex-col gap-2 p-4 rounded-2xl border text-left transition-all duration-200 cursor-pointer"
                          style={
                            active
                              ? { background: t.glow, borderColor: t.border, boxShadow: `0 0 16px ${t.glow}` }
                              : { background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.08)" }
                          }
                        >
                          <div className="w-9 h-9 flex items-center justify-center rounded-xl mb-1" style={{ background: active ? t.glow : "rgba(255,255,255,0.05)" }}>
                            <i className={`${t.icon} text-base`} style={{ color: active ? t.color : "rgba(255,255,255,0.35)" }} />
                          </div>
                          <p className="text-sm font-display font-bold text-white">{t.label}</p>
                          <p className="text-xs text-white/40 leading-relaxed">{t.desc}</p>
                          {active && <i className="ri-check-circle-fill text-sm mt-1" style={{ color: t.color as string }} />}
                        </button>
                      );
                    })}
                  </div>

                  {/* Tier selector */}
                  {(data.planType === "share" || data.planType === "direct") && (
                    <div
                      className="flex flex-col gap-3 p-5 rounded-2xl"
                      style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                    >
                      <p className="text-xs font-display font-semibold tracking-widest text-white/40 uppercase">
                        {data.planType === "share" ? "수익 쉐어형 — 세부 플랜" : "직접결제형 — 솔루션 규모"}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {renderTierCards(data.planType === "share" ? shareTiers : directTiers)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer — Steps */}
      {phase === "steps" && (
        <footer
          className="fixed bottom-0 left-0 right-0 z-50 h-20 flex items-center justify-between px-4 md:px-8"
          style={{ background: "rgba(6,8,15,0.92)", backdropFilter: "blur(20px)", borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          <button
            onClick={handlePrev}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-body border border-white/10 hover:border-white/20 text-white/50 hover:text-white transition-all cursor-pointer whitespace-nowrap ${step === 1 ? "opacity-0 pointer-events-none" : ""}`}
          >
            <i className="ri-arrow-left-line" /> 이전
          </button>

          <p className="text-xs text-white/30 font-body hidden sm:block">{STEPS[step - 1].label}</p>

          {step < TOTAL_STEPS ? (
            <button
              onClick={handleNext}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-display font-semibold transition-all cursor-pointer whitespace-nowrap ${canNext() ? "text-white" : "text-white/30 cursor-not-allowed"}`}
              style={canNext() ? { background: "linear-gradient(135deg, #A855F7, #7C3AED)", boxShadow: "0 0 20px rgba(168,85,247,0.3)" } : { background: "rgba(255,255,255,0.05)" }}
            >
              다음 단계 <i className="ri-arrow-right-line" />
            </button>
          ) : (
            <button
              onClick={handleNext}
              className={`flex items-center gap-2 px-7 py-3 rounded-full text-sm font-display font-semibold transition-all whitespace-nowrap ${canNext() ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`}
              style={canNext()
                ? { background: "linear-gradient(135deg, #A855F7 0%, #14F1D9 100%)", boxShadow: "0 0 28px rgba(168,85,247,0.4), 0 0 56px rgba(20,241,217,0.15)", color: "#fff" }
                : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.3)" }}
            >
              <i className="ri-flashlight-line" />
              진단 완료하기
            </button>
          )}
        </footer>
      )}

      {/* Footer — Email */}
      {phase === "email" && (
        <footer
          className="fixed bottom-0 left-0 right-0 z-50 h-20 flex items-center justify-between px-4 md:px-8"
          style={{ background: "rgba(6,8,15,0.92)", backdropFilter: "blur(20px)", borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          <button
            onClick={() => transitionTo("steps")}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-body border border-white/10 hover:border-white/20 text-white/50 hover:text-white transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" /> 이전
          </button>
          <p className="text-xs text-white/25 font-body hidden sm:block">이름과 이메일은 필수예요</p>
          <button
            type="submit" form="diagnosis-email-form"
            disabled={emailSubmitting || !emailData.name.trim() || !emailData.email.trim()}
            className={`flex items-center gap-2 px-7 py-3 rounded-full text-sm font-display font-semibold transition-all whitespace-nowrap ${!emailSubmitting && emailData.name.trim() && emailData.email.trim() ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`}
            style={!emailSubmitting && emailData.name.trim() && emailData.email.trim()
              ? { background: "linear-gradient(135deg, #A855F7 0%, #14F1D9 100%)", boxShadow: "0 0 28px rgba(168,85,247,0.4), 0 0 56px rgba(20,241,217,0.15)", color: "#fff" }
              : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.3)" }}
          >
            {emailSubmitting ? <><i className="ri-loader-4-line animate-spin" />전송 중...</> : <><i className="ri-send-plane-2-line" />빌딩 플랜 받기</>}
          </button>
        </footer>
      )}
    </div>
  );
}
